import type { AlertAppearance, Theme } from '@ksyos/design-system';
import type { ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { Alert, Box, Button, Link, List, Loader, Modal, Popover, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { PatientInsuranceHistory } from '@common/interfaces/api/patientsApi';
import type { IPatient } from '@common/interfaces/patient';
import type { PatientHasContractedCareForService } from '@common/interfaces/patientInsurance';
import { CustomControlMode, ServiceCode } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '@frontend/translate';

export interface OnChangeParams {
    isPatientInformed: boolean;
}

export interface InsuranceModalProps {
    activeInsurance?: PatientInsuranceHistory | null;
    isOpen: boolean;
    isPatientInformed?: boolean;
    mode: CustomControlMode;
    onCancel?: () => void;
    onChange?: (params: OnChangeParams) => void;
    onClose: () => void;
    patient: IPatient;
    patientInsurance?: PatientHasContractedCareForService;
    serviceCode: ServiceCode;
}

const checkboxText =
    'De patiënt is geïnformeerd en akkoord met het starten van de dienst en weet van de kosten die het met zich mee brengt';

function getFirstDeclarableActivityText(serviceCode: ServiceCode) {
    switch (serviceCode) {
        case ServiceCode.Headache:
            return 'de beoordeling van het hoofdpijnonderzoek';
        case ServiceCode.HearingLoss:
            return 'de beoordeling';
        case ServiceCode.LumbarRadiculopathy:
            return 'de beoordeling van de neuroloog';
        case ServiceCode.RespiAller:
            return 'de huidpriktest';
        case ServiceCode.OSAS:
            return 'het slaaponderzoek';
        default:
            return 'de dienst';
    }
}

export function InsuranceModal(props: Readonly<InsuranceModalProps>) {
    const language = getSelectedLanguage();
    const {
        activeInsurance,
        isOpen,
        isPatientInformed,
        mode,
        onCancel,
        onClose,
        patient,
        patientInsurance,
        serviceCode,
    } = props;
    const [alertAppearance, setAlertAppearance] = useState('info');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(isPatientInformed ?? false);

    const cancelOrClose = onCancel && mode === CustomControlMode.edit && !isPatientInformed ? onCancel : onCloseModal;

    useEffect(() => {
        setIsModalOpen(isOpen);
    }, [isOpen]);

    useEffect(() => {
        if (isCheckboxChecked) {
            setAlertAppearance('info');
        }
    }, [isCheckboxChecked]);

    useEffect(addInsuranceStatusToDatadog, [
        isModalOpen,
        patientInsurance?.hasContractedCare,
        patientInsurance?.isInsured,
    ]);

    if (!patientInsurance) {
        return (
            <Modal
                isOpen={isModalOpen}
                onClose={cancelOrClose}
                size="large"
            >
                <Modal.Body>
                    <Box
                        alignItems="center"
                        display="flex"
                        justifyContent="center"
                        paddingY={20}
                    >
                        <Loader size="small" />
                    </Box>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <Modal
            data-dd-privacy="allow"
            isOpen={isModalOpen}
            onClose={cancelOrClose}
            size="large"
        >
            <InsuranceModalContent
                activeInsurance={activeInsurance}
                patient={patient}
                patientInsurance={patientInsurance}
                serviceCode={serviceCode}
            />
            <Modal.Body>
                <Alert
                    appearance={alertAppearance as AlertAppearance}
                    noMargin={true}
                >
                    Akkoordverklaring patiënt {alertAppearance === 'warning' && ' nog niet ingevuld'}
                    {!isPatientInformed ? (
                        <Alert.Checkbox
                            checked={isCheckboxChecked}
                            label={
                                <Text
                                    size="sm"
                                    sx={{
                                        color:
                                            alertAppearance === 'warning'
                                                ? (theme: Theme) => theme.alert.warning.foregroundColor
                                                : (theme: Theme) => theme.alert.info.foregroundColor,
                                    }}
                                >
                                    {checkboxText}
                                </Text>
                            }
                            onChange={() => setIsCheckboxChecked(!isCheckboxChecked)}
                            sx={{ mt: 2 }}
                            value="info"
                        />
                    ) : (
                        <Text
                            size="sm"
                            sx={{
                                color: (theme: Theme) => theme.alert.info.foregroundColor,
                                mt: 1,
                            }}
                        >
                            {checkboxText}
                        </Text>
                    )}
                </Alert>
            </Modal.Body>
            <Modal.Footer style={{ paddingTop: 20 }}>
                {isPatientInformed ? (
                    <Button onClick={cancelOrClose}>Sluit</Button>
                ) : (
                    <>
                        <Button
                            onClick={cancelOrClose}
                            variant="outline"
                        >
                            {language.common.cancel}
                        </Button>
                        <Button
                            onClick={onCloseModal}
                            variant="solid"
                        >
                            {language.common.proceed}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );

    function onCloseModal() {
        if (isCheckboxChecked) {
            setIsModalOpen(false);
            if (props.onChange) {
                props.onChange({ isPatientInformed: true });
            }
            onClose();
        }

        if (!isCheckboxChecked) {
            setAlertAppearance('warning');
        }
    }

    function addInsuranceStatusToDatadog() {
        if (!isModalOpen || !patientInsurance) {
            return;
        }

        let modalName = '';
        if (patientInsurance.isInsured && patientInsurance.hasContractedCare) {
            modalName = 'insured-contracted';
        } else if (patientInsurance.isInsured && !patientInsurance.hasContractedCare) {
            modalName = 'insured-uncontracted';
        } else if (!patientInsurance.isInsured) {
            modalName = 'uninsured';
        }

        if (modalName.length > 0) {
            datadogRum.addAction(`insurance-modal-${modalName}`);
        }
    }
}

function ImageWithText({ imgSrc, imgAlt, text }: Readonly<{ imgAlt?: string; imgSrc?: string; text: ReactNode }>) {
    return (
        <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
        >
            <Box height="115px">
                {imgSrc && (
                    <img
                        alt={imgAlt}
                        height="115px"
                        src={imgSrc}
                    />
                )}
            </Box>
            <Text
                mt={8}
                textAlign="center"
            >
                {text}
            </Text>
        </Box>
    );
}

function InsuranceModalPopover({ serviceCode }: Readonly<{ serviceCode: ServiceCode }>) {
    const firstDeclarableActivityText = getFirstDeclarableActivityText(serviceCode);

    return (
        <Popover
            content={
                <List
                    size="small"
                    styleType="bullet"
                >
                    <List.Item>
                        De declaratieperiode gaat in op de datum dat {firstDeclarableActivityText} start. De tijd tussen
                        de aanvraag van de verwijzing en het uitvoeren van {firstDeclarableActivityText} kan vallen over
                        twee kalenderjaren. De patiënt kan in de tussentijd zijn overgestapt naar een andere
                        zorgverzekeraar.
                    </List.Item>
                    <List.Item>
                        In januari kan de patiënt als onverzekerd geregistreerd staan maar met terugwerkende kracht
                        alsnog een verzekering afsluiten.
                    </List.Item>
                    <List.Item>
                        Contracten met verzekeraars gaan per jaar en worden vaak in het voorjaar met terugwerkende
                        kracht vanaf 1 januari actief.
                    </List.Item>
                </List>
            }
        >
            veranderen rond de wisseling van het kalenderjaar.
        </Popover>
    );
}

type InsuranceModalContentProps = {
    activeInsurance?: PatientInsuranceHistory | null;
    patient: IPatient;
    patientInsurance: PatientHasContractedCareForService;
    serviceCode: ServiceCode;
};

function InsuranceModalContent({
    patient,
    patientInsurance,
    activeInsurance,
    serviceCode,
}: Readonly<InsuranceModalContentProps>) {
    if (patientInsurance.hasContractedCare && patientInsurance.isInsured) {
        return (
            <>
                <Modal.Header>
                    <span data-dd-privacy="mask">{getDisplayName(patient)}</span> ontvangt gecontracteerde zorg
                </Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <Text>
                        Ksyos heeft{' '}
                        <Text
                            as="span"
                            fontWeight="medium"
                        >
                            een contract met {activeInsurance?.uzovi?.name},
                        </Text>{' '}
                        de zorgverzekeraar van de patiënt.
                    </Text>
                    <Text>
                        De vergoeding van de zorg kan <InsuranceModalPopover serviceCode={serviceCode} />
                    </Text>
                    <Box
                        display="grid"
                        gap={8}
                        gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
                        mt={14}
                    >
                        <ImageWithText
                            imgAlt="vergoeding"
                            imgSrc="/assets/images/insurance-modal-vergoeding.svg"
                            text={
                                <>
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        100% van het gefactureerde bedrag wordt vergoed{' '}
                                    </Text>
                                    door de zorgverzekeraar.
                                </>
                            }
                        />
                        <ImageWithText
                            imgAlt="factuur"
                            imgSrc="/assets/images/insurance-modal-factuur.svg"
                            text={
                                <>
                                    De{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        zorgverzekering ontvangt de factuur{' '}
                                    </Text>
                                    rechtstreeks van Ksyos.
                                </>
                            }
                        />
                        <ImageWithText
                            imgAlt="patient-betaalt"
                            imgSrc="/assets/images/insurance-modal-patient-betaalt.svg"
                            text={
                                <>
                                    De patiënt betaalt bij Ksyos{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        nooit meer dan het eigen risico.
                                    </Text>
                                </>
                            }
                        />
                    </Box>
                </Modal.Body>
            </>
        );
    }

    if (!patientInsurance.hasContractedCare && patientInsurance.isInsured) {
        return (
            <>
                <Modal.Header>
                    <span data-dd-privacy="mask">{getDisplayName(patient)}</span> ontvangt ongecontracteerde zorg
                </Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <Text>
                        Ksyos heeft{' '}
                        <Text
                            as="span"
                            fontWeight="medium"
                        >
                            geen contract met {activeInsurance?.uzovi?.name ?? activeInsurance?.insurer?.name},
                        </Text>{' '}
                        de zorgverzekeraar van de patiënt.
                    </Text>
                    <Text>
                        De vergoeding van de zorg kan <InsuranceModalPopover serviceCode={serviceCode} />
                    </Text>
                    <Box
                        display="grid"
                        gap={14}
                        gridTemplateColumns="repeat(auto-fit, minmax(210px, 1fr))"
                        mt={14}
                    >
                        <ImageWithText
                            imgAlt="email-post"
                            imgSrc="/assets/images/insurance-modal-email-post.svg"
                            text={
                                <>
                                    Na afloop{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        ontvangt de patiënt de factuur van Ksyos.
                                    </Text>
                                </>
                            }
                        />
                        <ImageWithText
                            imgAlt="declareren"
                            imgSrc="/assets/images/insurance-modal-declareren.svg"
                            text={
                                <>
                                    De patiënt moet de{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        factuur zelf bij de zorgverzekeraar declareren.{' '}
                                    </Text>
                                    Bij een naturapolis scheldt Ksyos de eigen bijdrage kwijt.
                                </>
                            }
                        />
                        <ImageWithText
                            imgAlt="patient-betaalt"
                            imgSrc="/assets/images/insurance-modal-patient-betaalt.svg"
                            text={
                                <>
                                    De patiënt betaalt bij Ksyos{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        nooit meer dan het eigen risico.
                                    </Text>
                                </>
                            }
                        />
                    </Box>
                </Modal.Body>
            </>
        );
    }

    if (!patientInsurance.hasContractedCare && !patientInsurance.isInsured) {
        return (
            <>
                <Modal.Header>
                    Let op: <span data-dd-privacy="mask">{getDisplayName(patient)}</span> is niet verzekerd binnen
                    Nederland
                </Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <Text>
                        De vergoeding van de zorg kan <InsuranceModalPopover serviceCode={serviceCode} />
                    </Text>
                    <Box
                        display="grid"
                        gap={[4, 8]}
                        gridTemplateColumns={['1fr', null, 'minmax(250px, 1fr) auto minmax(250px, 1fr)']}
                        mt={14}
                    >
                        <ImageWithText
                            imgAlt="vergoeding"
                            imgSrc="/assets/images/insurance-modal-vergoeding.svg"
                            text={
                                <>
                                    Het onderzoek en eventuele behandeling wordt{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        volledig in rekening gebracht bij de patiënt.
                                    </Text>{' '}
                                    Bekijk de{' '}
                                    <Link
                                        href={'https://ksyos.nl/passantentarieven-ksyos/'}
                                        variant="externalLink"
                                    >
                                        passantentarieven
                                    </Link>
                                    .
                                </>
                            }
                        />
                        <Box display={['none', null, 'block']}>
                            <ImageWithText text="of" />
                        </Box>
                        <Box display={['block', null, 'none']}>
                            <Text textAlign="center">of</Text>
                        </Box>
                        <ImageWithText
                            imgAlt="declareren"
                            imgSrc="/assets/images/insurance-modal-declareren.svg"
                            text={
                                <>
                                    De patiënt moet de kosten eventueel{' '}
                                    <Text
                                        as="span"
                                        fontWeight="medium"
                                    >
                                        zelf declareren bij een buitenlandse verzekering.
                                    </Text>
                                </>
                            }
                        />
                    </Box>
                </Modal.Body>
            </>
        );
    }

    return null;
}
